function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
// ----------------------------------------------------------------------------
// Loyalty Pricing: reconcile the cart across a login-state change
// ----------------------------------------------------------------------------
// Loyalty pricing is a buyer-identity-gated automatic discount (the Discount
// Function only applies for signed-in customers). Shopify re-evaluates
// automatic discounts when the cart is WRITTEN to — not on login and not on a
// plain GET render. So a shopper who adds an item while logged out and then
// logs in keeps the pre-login (undiscounted) line prices on the cart page and
// the mini-cart until they next change the cart or reach checkout. The reverse
// is also true: logging out leaves a now-invalid discount showing.
//
// Fix: remember the login state across page loads and, when it flips with items
// already in the cart, issue a no-op cart write (re-send each line's current
// quantity). That forces Shopify to recompute the discount for the current
// buyer and fires `cart:modified`, which the mini-cart already listens to and
// repaints from. The full cart page only re-renders line/summary markup on a
// section refresh, so when the flip is detected while ON the cart page we reload
// once — the write has by then persisted the corrected prices server-side.
//
// The write persists the recompute regardless of which page detects the flip,
// so a later fresh visit to /cart renders correctly without a reload.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import { cart } from 'scripts/helpers/iamota-shopify-ajax';
import Debug from 'scripts/libs/debug';

// -------------------------------------
// Config
// -------------------------------------

// Persists across the login redirect (same origin). '1' = signed in, '0' = out.
var STATE_KEY = 'iamLoyaltyLoginState';

// Cart-page-only custom element — its presence means a reload is needed to
// refresh the full line/summary markup the mini-cart's event path doesn't cover.
var CART_PAGE_MARKER = 'iam-cart-items';

// -------------------------------------
// Helpers
// -------------------------------------

var currentState = () => window.loyaltyPricing && window.loyaltyPricing.loggedIn ? '1' : '0';
function readStoredState() {
  try {
    return localStorage.getItem(STATE_KEY);
  } catch (error) {
    return null; // storage blocked (private mode) — treat as "unknown"
  }
}
function writeStoredState(value) {
  try {
    localStorage.setItem(STATE_KEY, value);
  } catch (error) {
    // No persistence available; the sync just won't fire on the next load.
    Debug.warn('[loyalty-cart-sync] could not persist login state', error);
  }
}

/**
 * Force Shopify to recompute automatic discounts for the current buyer by
 * re-sending every line's existing quantity. Fires `cart:modified`, so the
 * mini-cart repaints itself. Resolves true when the cart had items.
 * @returns {Promise<Boolean>}
 */
function reconcileCart() {
  return _reconcileCart.apply(this, arguments);
} // -------------------------------------
// Init
// -------------------------------------
function _reconcileCart() {
  _reconcileCart = _asyncToGenerator(function* () {
    var state = yield cart.fetch();
    if (!state || !state.item_count) return false;

    // Echo current quantities keyed by line-item key (unambiguous across lines
    // that share a variant). A no-net-change update still triggers recompute.
    var updates = {};
    state.items.forEach(item => {
      updates[item.key] = item.quantity;
    });
    yield cart.update({
      updates
    });
    return true;
  });
  return _reconcileCart.apply(this, arguments);
}
function init() {
  return _init.apply(this, arguments);
}
function _init() {
  _init = _asyncToGenerator(function* () {
    // Only relevant when loyalty pricing is configured, and never in the theme
    // editor (its login state isn't a real storefront session).
    if (!window.loyaltyPricing) return;
    if (window.Shopify && window.Shopify.designMode) return;
    var previous = readStoredState();
    var current = currentState();

    // First seen, or no change — record and do nothing.
    if (previous === null || previous === current) {
      writeStoredState(current);
      return;
    }

    // Login state flipped. Record the new state FIRST so the reload below (or a
    // duplicate load) sees no pending transition and can't loop.
    writeStoredState(current);
    try {
      var hadItems = yield reconcileCart();
      if (hadItems && document.querySelector(CART_PAGE_MARKER)) {
        window.location.reload();
      }
    } catch (error) {
      Debug.warn('[loyalty-cart-sync] cart reconcile failed', error);
    }
  });
  return _init.apply(this, arguments);
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}